import React from "react";
import moment from "moment";
import GrupoHeader from "./GrupoHeader";
import TotalGeral from "./TotalGeral";
import BankingDetails from "./BankingDetails";
import { formatMoedaSimples, formatMoedaAmbas, formatMoney } from "../utils/formatMoney";

/**
 * Componente unificado de template para renderização de relatórios.
 * Serve tanto para a visualização na tela quanto para exportação PDF.
 * 
 * É completamente agnóstico quanto à origem dos dados — espera receber
 * um objeto já processado por uma Strategy, contendo:
 *   - titulo: string
 *   - periodoTitulo: string (opcional)
 *   - grupos: array de objetos { nome, rows/contas, totais, ... }
 *   - totaisGerais: objeto com os totais acumulados
 *   - tipo: "soa" | "contasPagar" | "liquidado"
 */

export default function RelatorioTemplate({
    titulo,
    periodoTitulo,
    grupos,
    totaisGerais,
    tipo,
    moeda = 5,
    mostrarAmbasMoedas = false,
    bancoData = null,
    clienteUnico = false,
    logoUrl = "https://i.ibb.co/MyqWLrY6/logo.png",
}) {

    const formatarValor = (valor, valorUsd) => {
        if (mostrarAmbasMoedas && valorUsd !== undefined) {
            return formatMoedaAmbas(valor, valorUsd);
        }
        return formatMoedaSimples(valor, moeda);
    };

    const isEnglish = parseInt(moeda) !== 5 || mostrarAmbasMoedas;
    const dataFormatada = clienteUnico ? "MMM Do YYYY" : "DD/MM/YY";

    // Mostra variação apenas se o relatório for do tipo liquidado e NÃO for exclusivamente em dólares
    const mostrarVariacao = tipo === "liquidado" && !(parseInt(moeda) === 6 && !mostrarAmbasMoedas);

    // ============================================================
    // RENDER: Tabela Unificada
    // ============================================================
    const renderTabelaUnificada = (grupo) => {
        const { nome, rows, totais, tipoPagamento, dadosPagamento, pessoaCnpj } = grupo;
        if (!rows || rows.length === 0) return null;

        const dadosPag = tipoPagamento
            ? { tipo: tipoPagamento, info: dadosPagamento, cnpj: pessoaCnpj }
            : null;

        return (
            <div key={nome} style={{ marginBottom: "25px", pageBreakInside: "avoid" }}>
                <GrupoHeader nome={nome} dadosPagamento={dadosPag} isEnglish={isEnglish} />

                <table className="tabela-padrao">
                    <thead>
                        <tr>
                            <th>{isEnglish ? (tipo === "liquidado" ? "PAYMENT DATE" : "DUE DATE") : (tipo === "liquidado" ? "DATA DE PAGTO" : "VENCIMENTO")}</th>
                            <th>OS</th>
                            <th>{isEnglish ? "DOCUMENT" : "DOCUMENTO"}</th>
                            <th>{isEnglish ? "VESSEL" : "NAVIO"}</th>
                            <th className="texto-direita">{isEnglish ? "ORIGINAL AMOUNT" : "VALOR ORIGINAL"}</th>
                            {mostrarVariacao && <th className="texto-direita">{isEnglish ? "EXC. VAR." : "VAR. CAMBIAL"}</th>}
                            <th className="texto-direita">{isEnglish ? "PAID" : "PAGO"}</th>
                            {tipo !== "liquidado" && <th className="texto-direita">{isEnglish ? "BALANCE" : "SALDO"}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr key={rowIndex} className={rowIndex % 2 === 0 ? "linha-par" : "linha-impar"}>
                                <td>{row.vencimento ? moment(row.vencimento).format(dataFormatada) : "N/A"}</td>
                                <td>{row.os}</td>
                                <td>{row.documento}</td>
                                <td>{row.navio || "N/A"}</td>
                                <td className="texto-direita">{formatarValor(row.bruto, row.brutoUsd)}</td>
                                {mostrarVariacao && <td className="texto-direita" style={{ color: row.variacao_cambial < 0 ? "red" : (row.variacao_cambial > 0 ? "green" : "inherit") }}>
                                    {formatarValor(row.variacao_cambial, row.variacao_cambialUsd)}
                                </td>}
                                <td className="texto-direita">{formatarValor(row.pago, row.pagoUsd)}</td>
                                {tipo !== "liquidado" && <td className="texto-direita" style={{ fontWeight: "bold" }}>
                                    {formatarValor(row.saldo, row.saldoUsd)}
                                </td>}
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="grupo-total">
                            <td colSpan="4" className="texto-direita">TOTAL:</td>
                            <td className="texto-direita">{formatarValor(totais?.bruto, totais?.brutoUsd)}</td>
                            {mostrarVariacao && <td className="texto-direita" style={{ color: totais?.variacao_cambial < 0 ? "red" : (totais?.variacao_cambial > 0 ? "green" : "inherit") }}>
                                {formatarValor(totais?.variacao_cambial, totais?.variacao_cambialUsd)}
                            </td>}
                            <td className="texto-direita">{formatarValor(totais?.pago, totais?.pagoUsd)}</td>
                            {tipo !== "liquidado" && <td className="texto-direita" style={{ fontWeight: "bold" }}>
                                {formatarValor(totais?.saldo, totais?.saldoUsd)}
                            </td>}
                        </tr>
                    </tfoot>
                </table>
            </div>
        );
    };

    // ============================================================
    // RENDER PRINCIPAL
    // ============================================================
    const isSOA = tipo === "soa";
    const isContasPagar = tipo === "contasPagar";

    const getTitulo = () => {
        if (!isEnglish) return titulo;
        switch (titulo) {
            case "CONTAS A PAGAR": return "ACCOUNTS PAYABLE";
            case "CONTAS PAGAS": return "PAID ACCOUNTS";
            case "CONTAS A RECEBER": return "ACCOUNTS RECEIVABLE";
            case "CONTAS RECEBIDAS": return "RECEIVED ACCOUNTS";
            default: return titulo;
        }
    };

    return (
        <div className="relatorio-padrao" style={{ zoom: 1 }}>
            {/* CABEÇALHO */}
            <div className="relatorio-header">
                <img className="relatorio-logo" src={logoUrl} alt="logo" />

                {isSOA ? (
                    <>
                        <h4>SOA - Statement of Accounts</h4>
                        <p>Issued on {moment().format("MMM Do YYYY")}</p>
                    </>
                ) : (
                    <h2 className="relatorio-titulo">{getTitulo()}</h2>
                )}

                {periodoTitulo && <p className="relatorio-periodo">{periodoTitulo}</p>}
            </div>

            {/* CORPO: GRUPOS */}
            {grupos.map((grupo, index) => renderTabelaUnificada(grupo))}

            <br />
            <div style={{ border: "1px solid #dee2e6" }}></div>
            <br />

            {/* TOTAL GERAL */}
            <TotalGeral
                tipo={isSOA ? "soa" : isContasPagar ? "contasPagar" : "liquidado"}
                totais={totaisGerais}
                moeda={moeda}
                mostrarAmbasMoedas={mostrarAmbasMoedas}
            />

            <br />
            <br />
            <br />

            {/* DETALHES BANCÁRIOS (apenas para SOA) */}
            {isSOA && <BankingDetails bancoData={bancoData} />}
        </div>
    );
}